<template>
  <div class='container container-padded-small order'>
    <h1>Order</h1>
    <div class="line" />

    <form @submit.prevent='validate_order' :class='{ errors: $v.$error }' novalidate>
      <div class='order-left'>
        <section class="cables">
          <h3>CAMERA CABLES</h3>
          <p>Your Arsenal 2 {{arsenal_model}} came with one free cable. Additional cables can be purchased for use with multiple camera models.</p>
          <Cables :arsenal_version="2" :arsenal="order.products.arsenal" :$v="$v.order.products.arsenal" />
        </section>

        <section class="contact-info">
          <h3 class="text-center">Let's get some contact info.</h3>

          <div class='row'>
            <div class='col s12 l7'>
              <input type='email' name='email' v-model.trim='order.email' placeholder='Email'>
              <div class='error' v-if='!$v.order.email.required'>Email is required</div>
              <div class='error' v-if='!$v.order.email.email'>Email is not the right format</div>
            </div>

            <div class='col s12 l5'>
              <input type='tel' name='phone' v-model.trim='order.phone_number' placeholder='Phone Number'>
              <div class='error' v-if='!$v.order.phone_number.required'>Phone Number is required</div>
              <div class='error' v-if='!$v.order.phone_number.minLength'>Phone Number must be more than 6 numbers.</div>
              <div class='error' v-if='!$v.order.phone_number.maxLength'>Phone Number must be less than 255 numbers.</div>
            </div>
          </div>
        </section>

        <section class='shipping-address'>
          <h3>SHIPPING ADDRESS</h3>
          <OrderAddress @update='address_updated' :address='order.shipping_address' :$v='$v.order.shipping_address' />
          <div class='errors' style="position:relative; top:-1rem">
            <div v-if='order.errors.shipping_address' class='error'>{{ order.errors.shipping_address }}</div>
            <div v-if='order.errors.calculate_tax' class='error'>{{ order.errors.calculate_tax }}</div>
            <div v-if='order.errors.promo_code' class='error'>{{ order.errors.promo_code }}</div>
          </div>
        </section>

        <ShippingMethod :preorder='preorder' :order='order' :$v='$v.order' :amazon_fulfillment_enabled='amazon_fulfillment_enabled' />

        <section class='payment-method'>
          <h3>PAYMENT METHOD</h3>
          <StripeCard ref="stripe" />
        </section>

        <section class="checkboxes">
          <label class='checkbox-label'>
            I have read and agreed to the terms outlined in the <span v-if="preorder">pre-</span>order <a href='/terms-and-conditions' target='_blank'>Terms and Conditions</a>.
            <input type='checkbox' name='terms' v-model='order.terms'>
            <span class="checkmark"></span>
            <div class='error' v-if='!$v.order.terms.required'>Please review and accept the terms before ordering</div>
          </label>

          <br />

          <label class='checkbox-label'>
            I agree to receive email notifications regarding product and feature updates, news, and promotions.  View <a href='/privacy-policy' target='_blank'>Privacy Policy</a>.
            <input type='checkbox' name='subscribe' v-model='order.subscribe'>
            <span class="checkmark"></span>
          </label>

          <div v-if='show_customs_checkbox'>
            <br />
            <label class='checkbox-label'>
              I understand that upon Arsenal's arrival in my country I am responsible for any customs/duties, VAT, other local taxes, and/or customs/duties handling fees charged by couriers via local customs.
              <input type='checkbox' name='agree_to_customs' v-model='order.agree_to_customs'>
              <span class="checkmark"></span>
              <div class='error' v-if='!$v.order.agree_to_customs.required'>You must agree to customs fees</div>
            </label>
          </div>

          <div v-if='preorder'>
            <br />
            <label class='checkbox-label'>
              I understand Arsenal 2’s Estimated Delivery Date is {{estimated_delivery}} and that any other items ordered will ship with Arsenal 2.
              <input type='checkbox' name='agree_to_preorder' v-model='order.agree_to_preorder'>
              <span class="checkmark"></span>
              <div class='error' v-if='!$v.order.agree_to_preorder.required'>You must agree to these terms</div>
            </label>
          </div>
        </section>

        <br /><br />
        <spinner-button :spin='submitting' class='btn btn-full btn-large place-order'>Place <span v-if="preorder">Pre-</span>Order</spinner-button>
      </div>

      <div class='order-right'>
        <Totals :order='order' :subtotal='subtotal' :shipping='shipping' :tax='tax' :total='raw_total' :promo_code_discount='promo_code_discount' :prices='prices' />

        <spinner-button :spin='submitting' class='btn btn-full btn-large place-order'>Place <span v-if="preorder">Pre-</span>Order</spinner-button>

        <div class="box" style="margin-top: 3rem">
          <img src="~app/assets/images/DPReview-logo.png">
          <h3>"Arsenal is artificial intelligence for your mirrorless or DSLR camera"</h3>
          <h4>- Digital Photography Review</h4>
        </div>

        <div class="box">
          <h3>We're confident you'll love Arsenal!</h3>
          <p>You can return Arsenal within 90 days for a full refund (minus shipping). Check out our <a href="/returns" target="_blank">return policy</a> and <a href="/warranty" target="_blank">warranty page</a> for details.</p>
        </div>
      </div>
    </form>

    <div class='bottom-padding' />

    <OrderAddressModal :show='address_modal' :order='order' :cancel='close_address_modal' :submit_via_modal='submit_via_modal' />
  </div>
</template>

<script>
  import * as Sentry from '@sentry/browser'
import countries from 'app/countries.json'
import { is_military_address, is_taxable_country, is_us_territory } from 'app/helpers/AddressHelpers'
import { noNumbers, noSpecialCharacters } from 'app/helpers/Validators'
import 'core-js/web/url-search-params'
import find from 'lodash/find'
import includes from 'lodash/includes'
import { email, maxLength, minLength, required } from 'vuelidate/lib/validators'

  import Cables from 'app/components/orders/Cables'
import OrderAddress from 'app/components/orders/OrderAddress'
import OrderAddressModal from 'app/components/orders/OrderAddressModal'
import ShippingMethod from 'app/components/orders/ShippingMethod'
import StripeCard from 'app/components/orders/StripeCard'
import Totals from 'app/components/orders/Totals'
import SpinnerButton from 'app/components/shared/SpinnerButton'
import OrderTrackingMixin from 'app/mixins/OrderTrackingMixin'
import TotalsMixin from 'app/mixins/TotalsMixin'

  export default {
    mixins: [TotalsMixin, OrderTrackingMixin],
    props: ['arsenal_model', 'prices', 'estimated_delivery', 'preorder', 'amazon_fulfillment_enabled'],
    async mounted () {
      const params = new URLSearchParams(window.location.search)
      const promo_code = params.get('promo_code')
      if (promo_code) this.validate_promo_code(promo_code)

      if (this.arsenal_model != 'Pro') {
        this.arsenal_model = 'Standard'
      }

      this.track_initiate_checkout()

      if (typeof dataLayer !== 'undefined') {
        dataLayer.push({'event': 'optimize.activate'})
      }
    },
    data () {
      return {
        arsenal_added: false,
        submitting: false,
        skip_address_validation: false,
        address_modal: false,
        order: {
          products: {
            arsenal: { quantity: 0, cables: [''] },
            arsenal_pro: { quantity: 0, cables: [] },
            phone_mount:   { quantity: 0 },
            battery:       { quantity: 0 },
            micro_sd_card: { quantity: 0 },
          },
          email: '',
          phone_number: '',
          shipping_address: {
            name: '',
            address_1: '',
            address_2: '',
            city: '',
            country: 'United States of America',
            state: '',
            zipcode: ''
          },
          shipping_method: null,
          tax_rate: null,
          shipping_taxable: null,
          promo_code: {},
          terms: false,
          subscribe: false,
          agree_to_customs: false,
          agree_to_preorder: false,
          errors: {}
        }
      }
    },
    validations: {
      order: {
        email: {
          required,
          email
        },
        phone_number: {
          required,
          minLength: minLength(6),
          maxLength: maxLength(255)
        },
        products: {
          arsenal: {
            cables: {
              $each: { required }
            }
          },
          arsenal_pro: {
            cables: {
              $each: { required }
            }
          },
        },
        shipping_address: {
          name: {
            required,
            maxLength: maxLength(100),
            noSpecialCharacters,
            noNumbers
          },
          address_1: {
            required,
            maxLength: maxLength(45),
            noSpecialCharacters
          },
          address_2: {
            maxLength: maxLength(45),
            noSpecialCharacters
          },
          city: {
            required,
            maxLength: maxLength(255),
            noSpecialCharacters
          },
          state: {
            presence () {
              const country = find(countries, (country) => { return country.name == this.order.shipping_address.country })
              if (country.states.length === 0) return true
              return !!this.order.shipping_address.state
            }
          },
          zipcode: {
            presence () {
              const country = find(countries, (country) => { return country.name == this.order.shipping_address.country })
              if (country.postal_code === false) return true
              return !!this.order.shipping_address.zipcode
            },
            minLength: minLength(3),
            maxLength: maxLength(255),
            format () {
              // Only validate US States
              if (this.order.shipping_address.country != 'United States of America') return true
              if (is_us_territory(this.order.shipping_address.state)) return true
              if (is_military_address(this.order.shipping_address.state)) return true

              if (!this.order.shipping_address.zipcode) return false
              let numbers = this.order.shipping_address.zipcode.match(/\d/g).join('')
              return numbers.length == 5 || numbers.length == 9
            }
          }
        },
        shipping_method: {
          required () {
            if (this.preorder) return true
            return !!this.order.shipping_method
          }
        },
        terms: {
          required
        },
        agree_to_customs: {
          required () {
            if (!this.show_customs_checkbox) return true
            return this.order.agree_to_customs
          }
        },
        agree_to_preorder: {
          required() {
            if (!this.preorder) return true
            return this.order.agree_to_preorder
          }
        }
      }
    },
    computed: {
      show_customs_checkbox () {
        return !is_taxable_country(this.order.shipping_address.country)
      },
    },
    methods: {
      close_address_modal () {
        this.reset_form()
        this.address_modal = false
      },
      submit_via_modal () {
        this.address_modal = false
        this.skip_address_validation = true
        this.submitting = false
        this.validate_order()
      },
      address_updated () {
        this.update_tax()
      },
      update_tax (force_update = false) {
        if (this.$v.order.shipping_address.$invalid) {
          this.$set(this.order, 'tax_rate', 0)
          this.$set(this.order, 'shipping_taxable', false)
          return
        }

        let body = {
          taxable:  true,
          street:   this.order.shipping_address.address_1,
          city:     this.order.shipping_address.city,
          state:    this.order.shipping_address.state,
          zip:      this.order.shipping_address.zipcode,
          country:  this.order.shipping_address.country,
          subtotal: this.subtotal,
          shipping: this.shipping
        }
        if (JSON.stringify(body) != this.last_update_tax_request || force_update) {
          this.last_update_tax_request = JSON.stringify(body)
          this.$set(this.order.errors, 'calculate_tax', null)

          this.$http.post('/orders/calculate_tax', body).then(response => {
            this.$set(this.order, 'tax_rate', response.body.tax_rate)
            this.$set(this.order, 'shipping_taxable', response.body.shipping_taxable)
          }, response => {
            this.$set(this.order.errors, 'calculate_tax', response.body.error)
          })
        }
      },
      validate_promo_code (promo_code) {
        this.$http.post('/orders/validate_promo_code', { promo_code }).then(response => {
          this.$set(this.order, 'promo_code', response.body)
        }, response => {
          this.$set(this.order.errors, 'promo_code', `An error occurred while trying to validate the promo code: ${response.body.error}`)
        })
      },
      validate_order () {
        if (this.submitting) return
        this.submitting = true

        if (this.isDefined('gtag')) {
          gtag('event', 'order submitted', { event_category: 'interactions' })
        }

        this.$v.$touch()

        if (this.subtotal == 0) {
          const message = 'Please select a product to purchase.'
          this.$toast(message, { horizontalPosition: 'center', verticalPosition: 'top' })
          this.reset_form()
          this.scroll_to_products()
          return
        }

        if (this.order.tax_rate == null) {
          this.update_tax(true)
        }

        this.$refs.stripe.create_token(result => {
          if (result.error) {
            const message = 'Some fields have errors. Please correct them and try again.'
            this.$toast(message, { horizontalPosition: 'center', verticalPosition: 'top' })
            this.reset_form()
            if (includes(this.order.products.arsenal.cables, '') || includes(this.order.products.arsenal_pro.cables, '')) {
              this.scroll_to_products()
            }
          } else {
            this.stripe_source_id = result.token.id
            this.card = result.token.card
            this.submit_order(result)
          }
        })
      },
      submit_order (result) {
        if (this.$v.$error) {
          let message = 'Some fields have errors.  Please correct them and try again.'
          this.$toast(message, { horizontalPosition: 'center', verticalPosition: 'top' })
          this.reset_form()
          if (includes(this.order.products.arsenal.cables, '') || includes(this.order.products.arsenal_pro.cables, '')) {
            this.scroll_to_products()
          }
          return
        }

        var body = {
          products:                {},
          email:                   this.order.email,
          phone_number:            this.order.phone_number,
          shipping_name:           this.order.shipping_address.name,
          shipping_address_1:      this.order.shipping_address.address_1,
          shipping_address_2:      this.order.shipping_address.address_2,
          shipping_city:           this.order.shipping_address.city,
          shipping_country:        this.order.shipping_address.country,
          shipping_state:          this.order.shipping_address.state,
          shipping_zipcode:        this.order.shipping_address.zipcode,
          promo_code:              this.order.promo_code.code,
          card_exp_month:          this.card.exp_month,
          card_exp_year:           this.card.exp_year,
          card_last_4:             this.card.last4,
          card_brand:              this.card.brand,
          stripe_source_id:        this.stripe_source_id,
          utm_json:                localStorage.getItem('utm_data') || '{}',
          intercom_visitor_id:     Intercom('getVisitorId'),
          skip_address_validation: this.skip_address_validation
        }

        if (this.arsenal_model == 'Pro') {
          body.products.arsenal_pro = this.order.products.arsenal
        } else {
          body.products.arsenal = this.order.products.arsenal
        }

        if (!!this.order.shipping_method) {
          body.shipping_method_id = this.order.shipping_method.id
          body.estimated_delivery = this.order.shipping_method.estimated_delivery
        }

        this.$http.post('/orders', body).then(response => {
          if (this.order.subscribe) this.subscribe(this.order.email)
          try {
            this.track_successful_order(response.body, this.prices)
          } catch (err) {
            // Order was successful, but there was an error with analytics
            // Don't prevent the order from redirecting to the success page
            Sentry.addBreadcrumb({
              message: '[CAUGHT] error with analytics (CableOnlyOrder)'
            });
            Sentry.captureException(err)
          }
          window.location = '/success'
        }, response => {
          if (response.status === 422) {
            this.address_modal = true
          } else {
            this.reset_form()
            let error = response.body.error || 'Something went wrong.  Please try again or contact support.'
            this.$toast(error, { horizontalPosition: 'center', verticalPosition: 'top' })
          }
        })
      },
      track_initiate_checkout () {
        let _this = this
        try {
          window.cookiePrivacy.onGrant('tracking', function() {
            if (_this.isDefined('fbq')) {
              const eventID = document.querySelector('meta[name="request_id"]').getAttribute('content')
              fbq('track', 'InitiateCheckout', {}, { eventID })
              initialize_facebook_advanced_matching()
              console.log('fb track initiateCheckout')
            }
          })
        } catch (e) {
          Sentry.addBreadcrumb({
            message: '[CAUGHT] error tracking InitiateCheckout'
          });
          Sentry.captureException(e)
        }
      },
      subscribe (email) {
        const body = {
          email: email,
          list_id: '3caf31968a1d7324efc349f0b45341a1',
          referrer: this.$cookie.get('referrer')
        }

        this.$http.post(`/subscribers`, body).then((response) => {
          console.log('posted', response)
        },
        response => {
          // something went wrong
        })
      },
      isDefined(ref) {
        return typeof window[ref] !== 'undefined'
      },
      scroll_to_products() {
        this.$el.scrollIntoView({ behavior: 'smooth' })
      },
      reset_form () {
        this.submitting = false
        this.skip_address_validation = false
      }
    },
    components: { Cables, OrderAddress, OrderAddressModal, ShippingMethod, StripeCard, SpinnerButton, Totals }
  }
</script>
